import React, { useCallback } from 'react';
import {
  conditionConfig,
  ModalData,
  OdontogramState,
  Tooth,
  ToothCondition,
} from '../odontogram.types';
import { useToast } from '../../../hooks/toast';
import {
  carregarDentesIniciais,
  getFaceTooth,
  hideToothFaces,
  showToothFaces,
} from '../odontogram.helpers';
import {
  OdontogramaTitle,
  OdontogramContainer,
  ArcadaContainer,
  ArcadaLabel,
  TeethRow,
  ToothWrapper,
  Instructions,
} from './styles';
import { useI18n } from '../../../shared/i18n';

const conditionTranslationKeyByValue: Partial<Record<ToothCondition, string>> =
  {
    [ToothCondition.AMALGAMA]: 'odontogramRender.conditions.amalgam',
    [ToothCondition.CARIES]: 'odontogramRender.conditions.caries',
    [ToothCondition.ENDODONTIA]: 'odontogramRender.conditions.endodontics',
    [ToothCondition.AUSENTE]: 'odontogramRender.conditions.missing',
    [ToothCondition.RESINA]: 'odontogramRender.conditions.resin',
    [ToothCondition.IMPLANTE]: 'odontogramRender.conditions.implant',
    [ToothCondition.SELANTE]: 'odontogramRender.conditions.sealant',
    [ToothCondition.COROA]: 'odontogramRender.conditions.crown',
    [ToothCondition.OUTROS]: 'odontogramRender.conditions.other',
  };

interface IRenderArcadaProps {
  arcada: Tooth[];
  condicaoSelecionada: ToothCondition | null;
  dispatch: React.Dispatch<any>;
  setTooth?: React.Dispatch<React.SetStateAction<ModalData | null>>;
  setModalCreate?: React.Dispatch<React.SetStateAction<boolean>>;
}

const RenderArcada: React.FC<IRenderArcadaProps> = ({
  arcada,
  condicaoSelecionada,
  dispatch,
  setTooth,
  setModalCreate,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();

  const getConditionLabel = useCallback(
    (condition: ToothCondition) => {
      const translationKey = conditionTranslationKeyByValue[condition];

      return translationKey ? t(translationKey) : '';
    },
    [t],
  );

  const handlePaint = useCallback(
    (dente: Tooth, faceValor: number) => {
      if (!condicaoSelecionada) {
        addToast({
          type: 'info',
          title: t('odontogramRender.toast.selectConditionTitle'),
          description: t('odontogramRender.toast.selectConditionDescription'),
        });
        return;
      }

      if (condicaoSelecionada === ToothCondition.LIMPAR) {
        const denteResetado = carregarDentesIniciais()[dente.type].find(
          (d) => d.id === dente.id,
        );

        if (denteResetado) {
          dispatch({
            type: 'UPDATE_DENTE',
            payload: { arcada: dente.type, dente: denteResetado },
          });
        }
        return;
      }

      const config = conditionConfig[condicaoSelecionada];
      const denteAtualizado: Tooth = {
        ...dente,
        updated: 1,
        currentState: condicaoSelecionada,
        face: '',
        classes: { ...dente.classes },
      };

      switch (condicaoSelecionada) {
        case ToothCondition.AUSENTE:
          hideToothFaces(denteAtualizado);
          denteAtualizado.classes['class6'] = config.classe;
          denteAtualizado.classes['class7'] = config.classe;
          break;

        case ToothCondition.IMPLANTE:
          hideToothFaces(denteAtualizado);
          denteAtualizado.classes['class10'] = config.classe;
          break;

        case ToothCondition.COROA:
          showToothFaces(denteAtualizado);
          denteAtualizado.classes['class9'] = config.classe;
          break;

        case ToothCondition.ENDODONTIA:
          showToothFaces(denteAtualizado);
          denteAtualizado.classes['class9'] = config.classe;
          break;
        case ToothCondition.OUTROS: {
          showToothFaces(denteAtualizado);
          denteAtualizado.classes['class9'] = config.classe;
          break;
        }
        default: {
          const classeKey = `class${faceValor}`;
          denteAtualizado.classes[classeKey] = config.classe;
          break;
        }
      }

      dispatch({
        type: 'UPDATE_DENTE',
        payload: { arcada: dente.type, dente: denteAtualizado },
      });

      setTooth &&
        setTooth({
          dente: denteAtualizado,
          face: getFaceTooth(dente, faceValor, condicaoSelecionada),
          estadoAtual: getConditionLabel(condicaoSelecionada),
          valor: faceValor,
        });
      setModalCreate && setModalCreate(true);
    },
    [condicaoSelecionada, addToast, getConditionLabel, t],
  );

  return (
    <TeethRow>
      {arcada.map((dente) => (
        <ToothWrapper key={dente.id}>
          <svg height="42" width="42" id={`dente-${dente.id}`}>
            <text x="18.48" y="6.72" fontFamily="Verdana" fontSize="9">
              {dente.numTooth}
            </text>
            <polygon
              points="8.4,8.4 42,8.4 33.6,16.8 16.8,16.8"
              className={dente.classes['class1']}
              onClick={() => handlePaint(dente, 1)}
            />
            <polygon
              points="42,8.4 42,42 33.6,33.6 33.6,16.8"
              className={dente.classes['class2']}
              onClick={() => handlePaint(dente, 2)}
            />
            <polygon
              points="42,42 8.4,42 16.8,33.6 33.6,33.6"
              className={dente.classes['class3']}
              onClick={() => handlePaint(dente, 3)}
            />
            <polygon
              points="8.4,42 16.8,33.6 16.8,16.8 8.4,8.4"
              className={dente.classes['class4']}
              onClick={() => handlePaint(dente, 4)}
            />
            <polygon
              points="16.8,16.8 33.6,16.8 33.6,33.6 16.8,33.6"
              className={dente.classes['class5']}
              onClick={() => handlePaint(dente, 5)}
            />
            <polygon
              points="8.4,12.6 12.6,8.4 42,37.8 37.8,42"
              className={dente.classes['class6']}
              onClick={() => handlePaint(dente, 6)}
            />
            <polygon
              points="37.8,8.4 42,12.6 12.6,42 8.4,37.8"
              className={dente.classes['class7']}
              onClick={() => handlePaint(dente, 7)}
            />
            <circle
              cx="25.2"
              cy="25.2"
              r="13.44"
              className={dente.classes['class8']}
              onClick={() => handlePaint(dente, 8)}
            />
            <circle
              cx="25.2"
              cy="25.2"
              r="16.8"
              className={dente.classes['class9']}
              onClick={() => handlePaint(dente, 9)}
            />
            <polygon
              points="42,8.4 33.6,8.4 8.4,21.84 8.4,26.88 38.64,26.88 8.4,42 16.8,42 42,30.24 42,23.52 11.76,23.52"
              className={dente.classes['class10']}
              onClick={() => handlePaint(dente, 10)}
            />
          </svg>
        </ToothWrapper>
      ))}
    </TeethRow>
  );
};

interface IOdontogramRenderProps {
  odontogramState: OdontogramState;
  condicaoSelecionada: ToothCondition | null;
  dispatch: React.Dispatch<any>;
  setModalCreate?: React.Dispatch<React.SetStateAction<boolean>>;
  setTooth?: React.Dispatch<React.SetStateAction<ModalData | null>>;
}

export const OdontogramRender: React.FC<IOdontogramRenderProps> = ({
  odontogramState,
  condicaoSelecionada,
  dispatch,
  setModalCreate,
  setTooth,
}) => {
  const { t } = useI18n();

  return (
    <OdontogramContainer>
      <OdontogramaTitle>{t('odontogramRender.title')}</OdontogramaTitle>
      <Instructions>{t('odontogramRender.instructions')}</Instructions>

      <ArcadaContainer>
        <ArcadaLabel>{t('odontogramRender.arches.upperAdult')}</ArcadaLabel>
        <RenderArcada
          arcada={odontogramState.adultoCima}
          dispatch={dispatch}
          condicaoSelecionada={condicaoSelecionada}
          setTooth={setTooth}
          setModalCreate={setModalCreate}
        />
      </ArcadaContainer>

      <ArcadaContainer>
        <ArcadaLabel>{t('odontogramRender.arches.upperChild')}</ArcadaLabel>
        <RenderArcada
          arcada={odontogramState.criancaCima}
          dispatch={dispatch}
          condicaoSelecionada={condicaoSelecionada}
          setTooth={setTooth}
          setModalCreate={setModalCreate}
        />
      </ArcadaContainer>

      <ArcadaContainer>
        <ArcadaLabel>{t('odontogramRender.arches.lowerChild')}</ArcadaLabel>
        <RenderArcada
          arcada={odontogramState.criancaBaixo}
          dispatch={dispatch}
          condicaoSelecionada={condicaoSelecionada}
          setTooth={setTooth}
          setModalCreate={setModalCreate}
        />
      </ArcadaContainer>

      <ArcadaContainer>
        <ArcadaLabel>{t('odontogramRender.arches.lowerAdult')}</ArcadaLabel>
        <RenderArcada
          arcada={odontogramState.adultoBaixo}
          dispatch={dispatch}
          condicaoSelecionada={condicaoSelecionada}
          setTooth={setTooth}
          setModalCreate={setModalCreate}
        />
      </ArcadaContainer>
    </OdontogramContainer>
  );
};
